import React from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import './CloneHistoryPanel.scss';
import {ArrDrawer} from '../../../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {ReactComponent as RightChevronIcon} from '../../../../../assets/images/icon-right-outline.svg';
import {useGetAIReviewJobCloneHistory} from '../../../../../hooks/ai/review/useReview';
import {IAIReviewCloneHistoryItem} from '../../../../../api/types/aiReview.type';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {useNavigate} from 'react-router-dom';

dayjs.extend(utc);

export type CloneHistoryPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  jobId?: number | string;
  onViewJobDetails?: (jobId: number) => void;
};

type CloneHistoryJob = {
  jobId: number;
  type?: 'Modified' | 'Original';
  updatedAt: string;
  createdBy: string;
  jobDetailsUrl?: string;
};

const mockCloneHistoryData: CloneHistoryJob[] = [
  {
    jobId: 10482,
    type: 'Modified',
    updatedAt: '2026/06/03 10:30:00 UTC',
    createdBy: 'Whitman, Avery',
  },
  {
    jobId: 10467,
    updatedAt: '2026/06/02 18:05:00 UTC',
    createdBy: 'Patterson, Nina',
  },
  {
    jobId: 10391,
    type: 'Modified',
    updatedAt: '2026/05/31 09:42:00 UTC',
    createdBy: 'Walker, Ethan',
  },
  {
    jobId: 10012,
    type: 'Original',
    updatedAt: '2026/05/29 14:16:00 UTC',
    createdBy: 'Garcia, Lina',
  },
];

const getCloneHistoryType = (
  isProtocolModified?: IAIReviewCloneHistoryItem['isProtocolModified']
): CloneHistoryJob['type'] => {
  const normalizedValue = String(isProtocolModified ?? '').toLowerCase();
  if (normalizedValue === 'true') {
    return 'Modified';
  }
  if (normalizedValue === 'original') {
    return 'Original';
  }
  return undefined;
};

const formatCloneHistoryDate = (date?: string) => {
  if (!date) {
    return '';
  }
  return dayjs.utc(date).format('YYYY/MM/DD HH:mm:ss [UTC]');
};

const mapCloneHistoryJob = (
  job: IAIReviewCloneHistoryItem
): CloneHistoryJob => ({
  jobId: job.jobId,
  type: getCloneHistoryType(job.isProtocolModified),
  updatedAt: formatCloneHistoryDate(job.lastModifiedAt ?? job.clonedAt),
  createdBy: job.createdByName ?? job.createdByName ?? '',
  jobDetailsUrl: job.jobDetailsUrl,
});

const getTypeChip = (type?: CloneHistoryJob['type']) => {
  if (type === 'Modified') {
    return (
      <Box
        component="span"
        className="clone-history-panel__type clone-history-panel__type--modified"
      >
        {t('ai.review.jobDetailsTab.cloneHistoryPanel.type.modified')}
      </Box>
    );
  }
  if (type === 'Original') {
    return (
      <Box
        component="span"
        className="clone-history-panel__type clone-history-panel__type--original"
      >
        {t('ai.review.jobDetailsTab.cloneHistoryPanel.type.original')}
      </Box>
    );
  }
  return null;
};

const CloneHistoryPanel: React.FC<CloneHistoryPanelProps> = ({
  isOpen,
  onClose,
  jobId,
  onViewJobDetails,
}) => {
  const {
    data: cloneHistoryResponse,
    isError,
    refetch,
  } = useGetAIReviewJobCloneHistory(jobId, isOpen);
  const navigate = useNavigate();
  const cloneHistoryJobs =
    cloneHistoryResponse?.cloneHistory?.map(mapCloneHistoryJob) ??
    mockCloneHistoryData;
  const showNoData = isError || jobId == null;

  const handleViewJobDetails = (selectedJobId: number) => {
    onClose();
    onViewJobDetails?.(selectedJobId);
    navigate(`/AI/Review/JobResults?jobId=${selectedJobId}`);
  };

  return (
    <ArrDrawer
      side="right"
      showButton={true}
      isOpen={isOpen}
      onClose={onClose}
      size="small"
      showHeader={false}
    >
      <Box className="clone-history-panel">
        <Box className="clone-history-panel__header">
          <ArrButton
            variant="secondary"
            size="medium"
            onClick={onClose}
            className="clone-history-panel__close-btn"
            dataTestId="clone-history-close-btn"
          >
            {t('ai.review.jobDetailsTab.cloneHistoryPanel.close')}
          </ArrButton>
        </Box>

        <Box className="clone-history-panel__title-row">
          <Typography className="clone-history-panel__title">
            {t('ai.review.jobDetailsTab.cloneHistoryPanel.title', {
              jobId,
            })}
          </Typography>
        </Box>

        {showNoData ? (
          <Box className="clone-history-panel__nodata">
            <Typography className="clone-history-panel__nodata-title">
              {t('ai.review.jobDetailsTab.cloneHistoryPanel.error.title')}
            </Typography>
            <Typography className="clone-history-panel__nodata-desc">
              {t('ai.review.jobDetailsTab.cloneHistoryPanel.error.description')}
              <br />
              {t('ai.review.jobDetailsTab.cloneHistoryPanel.error.refreshHint')}
            </Typography>
            <ArrButton
              variant="primary"
              size="medium"
              onClick={() => refetch()}
              dataTestId="clone-history-refresh-btn"
            >
              {t('ai.review.jobDetailsTab.cloneHistoryPanel.refresh')}
            </ArrButton>
          </Box>
        ) : (
          <Box className="clone-history-panel__list">
            {cloneHistoryJobs.map((job) => (
              <Box className="clone-history-panel__item" key={job.jobId}>
                <Box>
                  <Typography className="clone-history-panel__jobid">
                    {t('ai.review.jobDetailsTab.cloneHistoryPanel.jobId', {
                      jobId: job.jobId,
                    })}
                    {getTypeChip(job.type)}
                  </Typography>
                  <Typography className="clone-history-panel__timestamp">
                    {job.updatedAt}
                    {job.createdBy ? ` - ${job.createdBy}` : ''}
                  </Typography>
                </Box>
                <ArrButton
                  variant="secondary"
                  size="xs"
                  className="clone-history-panel__view-btn"
                  onClick={() => handleViewJobDetails(job.jobId)}
                  dataTestId={`clone-history-view-details-btn-${job.jobId}`}
                  endIcon={
                    <RightChevronIcon className="clone-history-panel__view-btn-icon" />
                  }
                >
                  {t(
                    'ai.review.jobDetailsTab.cloneHistoryPanel.viewJobDetails'
                  )}
                </ArrButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </ArrDrawer>
  );
};

export default CloneHistoryPanel;
